import { FC, useState } from 'react';
import { AutocompleteInput, Modal } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePrompts } from 'store/prompts';
import { useDebouncedCallback } from 'use-debounce';

interface Values {
  promptId: string;
}

interface Props extends ModalProps {
  closeModal: (
    param?: { action: 'CLOSE' } | { action: 'SELECT'; promptId: string },
  ) => void;
}

export const PromptSelectModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();

  const [promptSearch, setPromptSearch] = useState('');
  const handlePromptSearch = useDebouncedCallback(setPromptSearch, 300);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: {
      promptId: '',
    },
  });

  const { data: prompts } = usePrompts({ search: promptSearch });

  const onSubmit: SubmitHandler<Values> = (values) => {
    closeModal({
      action: 'SELECT',
      promptId: values.promptId,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('prompts.title', { count: 2 })}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('employees.select'),
            type: 'submit',
            disabled: !isDirty,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="promptId"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <AutocompleteInput
                  label={t('prompts.title', { count: 1 })}
                  name={name}
                  value={value}
                  multiple={false}
                  required
                  onChange={onChange}
                  options={
                    prompts?.items.map((prompt) => ({
                      label: prompt.name,
                      value: prompt.id,
                    })) ?? []
                  }
                  onInputChange={handlePromptSearch}
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
